import { AfterViewInit, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltip,
  MatTooltipDefaultOptions,
  MatTooltipModule
} from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Approval } from '../../core/types';
import { AsyncPipe, DatePipe, NgClass, SlicePipe } from '@angular/common';
import {
  getStatusColor,
  getStatusText,
  StateFilterCBComponent
} from '../../utils/component/state-filter-combobox/state-cb.component';
import { AuthState } from '../../core/authentication/auth.state';
import { getMinFilterLengthText } from '../../core/util';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of, switchMap, tap } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { messageTypes, WidgetMessageService } from '../../core/services/widget-message.service';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { MatDialog } from '@angular/material/dialog';
import { ModuleEntity } from '../module.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { SubjectEntity } from '../../subject/subject.entity';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { ModuleEntityService } from '../services/module.entity.service';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { SchoolClassEntity } from '../../core/global-enums/school-class/school-class.entity';
import { SchoolClassEntityService } from '../../core/global-enums/school-class/services/school-class.entity.service';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { FormDialogModuleComponent, MetadataRow } from '../form-dialog-module/form-dialog-module.component';
import {
  ModuleLearningUnitMetadataEntityService
} from '../services/module-learning-unit-metadata.entity.service';
import { ModuleLearningUnitMetadataEntity } from '../module-learning-unit-metadata.entity';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { LocalStorageService } from '../../core/services/local-storage.service';

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 1000,
  hideDelay: 100,
  touchendHideDelay: 100,
};

@Component({
  selector: 'pixl-main-module-management',
  imports: [
    MatIcon,
    MatSortModule,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    FreeFilterInputComponent,
    NgClass,
    TranslocoModule,
    MatTooltip,
    AsyncPipe,
    MatPaginator,
    TableLoadingSpinnerComponent,
    SlicePipe,
    StateFilterCBComponent,
    MultiComboEntitySearchComponent,
    DatePipe
  ],
  templateUrl: './main-module.component.html',
  styleUrl: './main-module.component.scss',
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults }]
})
export class MainModuleComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  private moduleService = inject(ModuleEntityService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private schoolClassService = inject(SchoolClassEntityService);
  private metadataEntityService = inject(ModuleLearningUnitMetadataEntityService);
  private localStorageService = inject(LocalStorageService);

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  modules$!: Observable<ModuleEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  learningUnits$!: Observable<LearningUnitEntity[]>;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  schoolClasses$!: Observable<SchoolClassEntity[]>;

  public editor$ = this.store.select(AuthState.getEditor);

  public readonly freeFilterLen = 3;
  private freeFilter = '';
  private consumerFilter: string[] = [];
  private subjectFilter: string[] = [];
  private statusFilter = '';
  protected learningUnitFilter: string[] = [];

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;
  searchQueryLearningUnits: string | null = null;

  private refresh$ = new BehaviorSubject<void>(undefined);
  public currentSelectedRow = signal<string | undefined>(undefined);

  displayedColumns: string[] = ['actions', 'id', 'preTitle', 'title', 'description', 'consumer', 'subjects', 'schoolClasses', 'learningUnits', 'status', 'created_at'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private destroyRef = inject(DestroyRef);
  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  ngOnInit() {
    this.loadRelationData();

    this.editor$.subscribe(editor => {
      if (editor && editor.role === 'zavAdmin') {
        this.displayedColumns.push('content');
      }
    });

    this.modules$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'consumer,subjects,learningUnits,schoolClasses',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'title'
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[titleLike]'] = this.freeFilter.trim();
        }

        if (this.consumerFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[consumer][id]'] = this.consumerFilter.join(',');
        }

        if (this.subjectFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[subjects][name]'] = this.subjectFilter.join(',');
        }

        if (this.learningUnitFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[learningUnits][title]'] = this.learningUnitFilter.join(',');
        }

        if (this.statusFilter?.length > 0) {
          query['page[number]'] = 0;
          query['filter[status]'] = this.statusFilter.trim();
        }

        return from(this.moduleService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.moduleService.allByQuery(query))),
          map((modules: ModuleEntity[]) => modules.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.moduleService.stateClass) as EntityStateModel<ModuleEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const learningUnitQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningUnitService.allByQuery(learningUnitQuery)).pipe(
          map(paginated => {
            return [...this.selectedLearningUnits, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );

    const schoolClassQuery = { sort: 'grade' };
    this.schoolClasses$ = from(
      this.schoolClassService.ensureLoaded(schoolClassQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.schoolClassService.allByQuery(schoolClassQuery))
      )
    );
  }

  onAdd(): void {
    const dialogRef = this.dialog.open(FormDialogModuleComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: Partial<ModuleEntity> | undefined) => {
      if (result) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const metadataRows: MetadataRow[] | undefined = (result as any)._metadataRows;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (result as any)._metadataRows;

        const newEntityInstance = Object.assign(new ModuleEntity(), result);
        const created = await this.moduleService.create(newEntityInstance);

        if (metadataRows) {
          await this.saveMetadataRows(created.id, metadataRows);
        }

        this.store.dispatch(new InvalidateLoadedQueries());
        this.refresh$.next();

        this.showMessage('Modul erfolgreich erstellt.');
      }
    });
  }

  onEdit(module: ModuleEntity): void {
    this.currentSelectedRow.set(module.id);

    const dialogRef = this.dialog.open(FormDialogModuleComponent, {
      width: '1200px',
      maxWidth: 'none',
      data: { ...module }
    });

    dialogRef.afterClosed().subscribe(async (updatedModule: ModuleEntity | undefined) => {
      if (!updatedModule) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const metadataRows: MetadataRow[] | undefined = (updatedModule as any)._metadataRows;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (updatedModule as any)._metadataRows;

      await firstValueFrom(this.moduleService.update(updatedModule));

      if (metadataRows && updatedModule.id) {
        await this.syncMetadataRows(updatedModule.id, metadataRows);
        this.store.dispatch(new InvalidateLoadedQueries());
      }

      this.refresh$.next();

      this.showMessage('Modul erfolgreich editiert.');
    });
  }

  async onCopy(module: ModuleEntity) {
    this.currentSelectedRow.set(module.id);
    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(module.title),
            headerText: 'Modul kopieren',
            afterNameText: 'wirklich kopieren?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    const copyEntityInstance = Object.assign(new ModuleEntity(), module);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).consumer = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...(copyEntityInstance as any).consumer,
      type: 'consumers',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.title = `${copyEntityInstance.title} (Kopie)`;
    await this.moduleService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Modul erfolgreich dupliziert.');
  }

  async onDelete(module: ModuleEntity) {
    if (!module?.id) {
      console.error('ID is undefined!', module);
      return;
    }

    this.currentSelectedRow.set(module.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: String(module.title),
            headerText: 'Modul löschen',
            afterNameText: 'wirklich löschen?'
          }
        })
        .afterClosed()
    );

    if (!confirmed) return;

    await firstValueFrom(this.moduleService.delete(module.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.showMessage('Modul erfolgreich gelöscht.');
  }

  private async saveMetadataRows(moduleId: string, rows: MetadataRow[]): Promise<void> {
    for (const row of rows) {
      for (const luId of row.learningUnitIds) {
        const entry = Object.assign(new ModuleLearningUnitMetadataEntity(), {
          cantonKey: row.cantonKey,
          professionKey: row.professionKey,
          module: { type: 'modules', id: moduleId },
          learningUnit: { type: 'learning-units', id: luId },
        });
        await this.metadataEntityService.create(entry);
      }
    }
  }

  private async syncMetadataRows(moduleId: string, rows: MetadataRow[]): Promise<void> {
    // Invalidate cached metadata queries so ensureLoaded fetches fresh data
    this.store.dispatch(new InvalidateLoadedQueries());

    // Delete all existing metadata for this module
    const metadataQuery = { 'filter[module]': moduleId };
    await this.metadataEntityService.ensureLoaded(metadataQuery);
    const existing = this.store.selectSnapshot(
      this.metadataEntityService.allByQuery(metadataQuery)
    ) as ModuleLearningUnitMetadataEntity[];

    for (const entry of existing) {
      await firstValueFrom(this.metadataEntityService.delete(entry.id));
    }

    // Create new entries
    await this.saveMetadataRows(moduleId, rows);
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public getStatusColor(approval: Approval): string {
    return getStatusColor(approval);
  }

  public getStatusText(approval: Approval): string {
    return this.translocoService.translate(getStatusText(approval));
  }

  getSubjectNames(subjects: { id: string }[]): string[] {
    if (!subjects?.length) return [];

    const state = this.store.selectSnapshot(this.subjectService.stateClass);

    return subjects.map(subject => {
      const entity = this.subjectService.stateClass.byId(state)(subject.id);
      return entity?.name || subject.id;
    });
  }

  getSchoolClassNames(schoolClasses: { id: string }[]): string[] {
    if (!schoolClasses?.length) return [];

    const state = this.store.selectSnapshot(this.schoolClassService.stateClass);

    return schoolClasses.map(schoolClass => {
      const entity = this.schoolClassService.stateClass.byId(state)(schoolClass.id);
      return entity?.grade ? `Klasse ${entity.grade}` : schoolClass.id;
    });
  }

  getLearningUnitNames(learningUnits: { id: string }[]): string[] {
    if (!learningUnits?.length) return [];

    const state = this.store.selectSnapshot(this.learningUnitService.stateClass);

    return learningUnits.map(learningUnit => {
      const entity = this.learningUnitService.stateClass.byId(state)(learningUnit.id);
      return entity?.title || learningUnit.id;
    });
  }

  getConsumerName(consumerId: string): string {
    if (!consumerId) return '---';

    const state = this.store.selectSnapshot(this.consumerService.stateClass);
    const consumer = this.consumerService.stateClass.byId(state)(consumerId);

    return consumer?.name || consumerId;
  }

  public setFreeFilter(input: string): void {
    this.currentSelectedRow.set(undefined);
    this.freeFilter = input;
    this.refresh$.next();
  }

  public setConsumerFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.consumerFilter = selected;
    this.refresh$.next();
  }

  public setSubjectFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.subjectFilter = selected;
    this.refresh$.next();
  }

  public setStatusFilter(selected: string): void {
    this.currentSelectedRow.set(undefined);
    this.statusFilter = selected;
    this.refresh$.next();
  }

  receiveLearningUnitData(selected: LearningUnitEntity[] | null) {
    this.selectedLearningUnits = selected ?? [];
    const selectedTitles = (selected ?? []).map(lu => lu.title);
    this.setLearningUnitFilter(selectedTitles);
  }

  public setLearningUnitFilter(selected: string[]): void {
    this.currentSelectedRow.set(undefined);
    this.learningUnitFilter = selected;
    this.refresh$.next();
  }

  async receiveLearningUnitQuery(query: string) {
    this.searchQueryLearningUnits = query?.trim() || null;

    if (this.searchQueryLearningUnits && this.searchQueryLearningUnits.length > 3) {
      // search mode
      const searchParams = { 'filter[titleLike]': this.searchQueryLearningUnits };
      await this.learningUnitService.ensureLoaded(searchParams);

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const learningUnitPageQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };
      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [...this.selectedLearningUnits, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreLearningUnits(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningUnits) {
      const learningUnitPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.learningUnitService.ensureLoaded(learningUnitPageQuery);

      const currentLearningUnits = await firstValueFrom(this.learningUnits$ ?? of([]));

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedLearningUnits,
            ...currentLearningUnits,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  get learningUnitLabels(): string {
    return this.learningUnitFilter?.length ?
      this.learningUnitFilter.join(', ') :
      'Alle Lerneinheiten';
  }

  trackById(index: number, module: ModuleEntity): string {
    return module.id;
  }
}
